import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PackagePlus, ScanLine, CheckCircle2, X, Users, BarChart3, Building2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";

const STAFF_STEPS = [
  {
    Icon: PackagePlus,
    title: "hc.registerAFoundItem",
    text: "hc.openDepositInTheMenu",
  },
  {
    Icon: ScanLine,
    title: "hc.storeFindWithQr",
    text: "hc.eachItemGetsAQr",
  },
  {
    Icon: CheckCircle2,
    title: "hc.returnToTheOwner",
    text: "hc.whenACustomerComesTo",
  },
];

const ADMIN_STEPS = [
  {
    Icon: Users,
    title: "hc.manageYourTeam",
    text: "hc.fromTeamInviteYourColleagues",
  },
  {
    Icon: Building2,
    title: "hc.setUpYourEstablishments",
    text: "hc.addAndConfigureYourEstablishments",
  },
  {
    Icon: BarChart3,
    title: "hc.trackYourStatistics",
    text: "hc.checkStatisticsToTrackDeposited",
  },
];

export default function StaffOnboarding({ onClose }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const { partner } = useAuth();
  const isAdmin = partner?.role === "admin";
  const STEPS = isAdmin ? ADMIN_STEPS : STAFF_STEPS;
  const [i, setI] = useState(0);
  const last = i === STEPS.length - 1;
  const step = STEPS[i];
  const primaryLabel = isAdmin ? t("hc.manageMyTeam") : t("hc.depositAnItem");
  const primaryRoute = isAdmin ? "/b2b/team" : "/b2b/deposit";

  async function finish(go) {
    try { await api("/auth/me/onboarded", { method: "POST" }); } catch { /* ignore */ }
    onClose?.();
    if (go) navigate(primaryRoute);
  }

  return (
    <div className="fixed inset-0 z-[60] bg-findo-navy/60 backdrop-blur-sm grid place-items-center p-4" data-testid="staff-onboarding">
      <div className="bg-white rounded-3xl w-full max-w-md p-7 relative card-shadow">
        <button onClick={() => finish(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700" data-testid="onboarding-close" aria-label={t("hc.close")}>
          <X size={20} />
        </button>
        <p className="text-xs font-semibold text-findo-indigo uppercase tracking-wide">{t("hc.welcomeToFindo")}</p>
        <h2 className="font-display text-2xl font-bold text-findo-navy mt-1">{isAdmin ? t("hc.yourFirstStepsAdmin") : t("hc.yourFirstSteps")}</h2>

        <div className="mt-6 flex flex-col items-center text-center gap-3" data-testid={`onboarding-step-${i}`}>
          <div className="h-16 w-16 rounded-2xl findo-gradient grid place-items-center text-white">
            <step.Icon size={30} />
          </div>
          <h3 className="font-display text-lg font-bold text-slate-800">{t(step.title)}</h3>
          <p className="text-sm text-slate-500 leading-relaxed">{t(step.text)}</p>
        </div>

        <div className="flex justify-center gap-1.5 mt-6">
          {STEPS.map((_, idx) => (
            <span key={idx} className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-findo-indigo" : "w-1.5 bg-slate-200"}`} />
          ))}
        </div>

        <div className="mt-6 flex gap-2">
          <Button variant="outline" className="flex-1 rounded-xl font-semibold" onClick={() => finish(false)} data-testid="onboarding-skip">
            {t("hc.skip")}
          </Button>
          {last ? (
            <Button className="flex-1 rounded-xl font-semibold" onClick={() => finish(true)} data-testid="onboarding-start">
              {primaryLabel}
            </Button>
          ) : (
            <Button className="flex-1 rounded-xl font-semibold" onClick={() => setI(i + 1)} data-testid="onboarding-next">
              {t("hc.next")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
